<template>
  <div class="board-conversations">
    <div class="conversations-title">
      <h2>{{ board.title }} {{ chatListLoading ? '-' : `- ${count}` }}</h2>
    </div>
    <div
      ref="activeConversation"
      class="conversations-list"
      :class="{ 'is-context-menu-open': isContextMenuOpen }"
      :draggable="board.key != 'inboxes'"
      @drop="onDrop"
      @dragover.prevent
      @dragenter.prevent
    >
      <conversation-card
        v-for="chat in conversationList"
        :key="chat.id"
        :active-label="label"
        :team-id="teamId"
        :folders-id="foldersId"
        :chat="chat"
        :conversation-type="conversationType"
        :show-assignee="showAssigneeInConversationCard"
        :selected="isConversationSelected(chat.id)"
        :disable-checkbox="true"
        :board-selected="board.key"
        @select-conversation="selectConversation"
        @de-select-conversation="deSelectConversation"
        @assign-agent="onAssignAgent"
        @assign-team="onAssignTeam"
        @assign-label="onAssignLabels"
        @update-conversation-status="toggleConversationStatus"
        @context-menu-toggle="onContextMenuToggle"
        @mark-as-unread="markAsUnread"
        @assign-priority="assignPriority"
      />

      <div v-if="chatListLoading" class="text-center">
        <span class="spinner" />
      </div>

      <woot-button
        v-if="!hasCurrentPageEndReached && !chatListLoading"
        variant="clear"
        size="expanded"
        @click="loadMoreConversations"
      >
        {{ $t('CHAT_LIST.LOAD_MORE_CONVERSATIONS') }}
      </woot-button>

      <p
        v-if="showEndOfListMessage"
        class="text-center text-muted end-of-list-text"
      >
        {{ $t('CHAT_LIST.EOF') }}
      </p>
    </div>

    <woot-modal
      :show.sync="showClosingReasonPopup"
      :on-close="hideClosingReasonPopup"
    >
      <add-closing-reason
        :inbox="closingReasonInbox"
        :current-chat="closingReasonChat"
        :options="closingReasonOptions"
        @on-close="hideClosingReasonPopup"
      />
    </woot-modal>

    <woot-modal
      :show.sync="showResolveBlockedModal"
      :on-close="closeResolveBlockedModal"
    >
      <opportunity-resolve-blocked-modal
        v-if="resolveBlockedOpportunity"
        :opportunity="resolveBlockedOpportunity"
        @close="closeResolveBlockedModal"
      />
    </woot-modal>
  </div>
</template>

<script>
import { mapGetters } from 'vuex';
import { parseAPIErrorResponse } from 'dashboard/store/utils/api';

import ConversationCard from '../../../../components/widgets/conversation/ConversationCard';
import AddClosingReason from 'dashboard/routes/dashboard/settings/closingreasons/Modal';
import OpportunityResolveBlockedModal from 'dashboard/components/widgets/conversation/OpportunityResolveBlockedModal.vue';
import advancedFilterTypes from '../../../../components/widgets/conversation/advancedFilterItems';
import alertMixin from 'shared/mixins/alertMixin';
import eventListenerMixins from 'shared/mixins/eventListenerMixins';

import timeMixin from '../../../../mixins/time';
import conversationMixin from '../../../../mixins/conversations';
import wootConstants from '../../../../constants/globals';
import filterQueryGenerator from '../../../../helper/filterQueryGenerator.js';
import ConversationApi from '../../../../api/inbox/conversation';
import { CONVERSATION_EVENTS } from '../../../../helper/AnalyticsHelper/events';
import { isConversationStatusChangeBlocked } from 'dashboard/helper/conversationClosingReasonHelper';
import { fetchOpenOpportunityForConversation } from 'dashboard/helper/opportunityResolveGuard';
import { FEATURE_FLAGS } from 'dashboard/featureFlags';

export default {
  components: {
    ConversationCard,
    AddClosingReason,
    OpportunityResolveBlockedModal,
  },
  mixins: [timeMixin, conversationMixin, eventListenerMixins, alertMixin],
  props: {
    conversationInbox: {
      type: [String, Number],
      default: 0,
    },
    teamId: {
      type: [String, Number],
      default: 0,
    },
    label: {
      type: String,
      default: '',
    },
    conversationType: {
      type: String,
      default: '',
    },
    foldersId: {
      type: [String, Number],
      default: 0,
    },
    showConversationList: {
      default: true,
      type: Boolean,
    },
    isOnExpandedLayout: {
      default: false,
      type: Boolean,
    },
    status: {
      type: String,
      default: 'all',
    },
    board: {
      type: Object,
      default: () => ({}),
    },
  },
  data() {
    return {
      activeAssigneeTab: wootConstants.ASSIGNEE_TYPE.ALL,
      showAdvancedFilters: false,
      advancedFilterTypes: advancedFilterTypes.map(filter => ({
        ...filter,
        attributeName: this.$t(`FILTER.ATTRIBUTES.${filter.attributeI18nKey}`),
      })),
      foldersQuery: {},
      showAddFoldersModal: false,
      showDeleteFoldersModal: false,
      selectedConversations: [],
      selectedInboxes: [],
      isContextMenuOpen: false,
      count: 0,
      showClosingReasonPopup: false,
      closingReasonConversationId: null,
      closingReasonOptions: {},
      chatListLoading: false,
      showResolveBlockedModal: false,
      resolveBlockedOpportunity: null,
    };
  },
  computed: {
    ...mapGetters({
      currentChat: 'getSelectedChat',
      chatLists: 'getAllConversations',
      mineChatsList: 'getMineChats',
      allChatList: 'getAllStatusChats',
      unAssignedChatsList: 'getUnAssignedChats',
      currentUserID: 'getCurrentUserID',
      activeInbox: 'getSelectedInbox',
      conversationStats: 'conversationStats/getStats',
      appliedFilters: 'getAppliedConversationFilters',
      folders: 'customViews/getCustomViews',
      inboxes: 'inboxes/getInboxes',
      currentRole: 'getCurrentRole',
      getChatListLoadingByGroupedBoard: 'getChatListLoadingByGroupedBoard'
    }),
    hasAppliedFilters() {
      return this.appliedFilters.length !== 0;
    },
    savedFoldersValue() {
      if (this.hasActiveFolders) {
        const payload = this.activeFolder.query;
        this.fetchSavedFilteredConversations(payload);
      }
      return {};
    },
    showAssigneeInConversationCard() {
      return (
        this.hasAppliedFilters ||
        this.activeAssigneeTab === wootConstants.ASSIGNEE_TYPE.ALL
      );
    },
    inbox() {
      return this.$store.getters['inboxes/getInbox'](this.activeInbox);
    },
    currentPage() {
      return this.$store.getters['conversationPage/getCurrentPageFilter'](
        this.boardPageFilterKey
      );
    },
    currentPageFilterKey() {
      return this.hasAppliedFilters ? 'appliedFilters' : this.activeAssigneeTab;
    },
    boardKey() {
      return `${this.board.key}_${this.board.value}`;
    },
    boardPageFilterKey() {
      return `${this.currentPageFilterKey}_${this.boardKey}`;
    },
    currentFiltersPage() {
      return this.$store.getters['conversationPage/getCurrentPageFilter'](
        this.boardPageFilterKey
      );
    },
    hasCurrentPageEndReached() {
      return this.$store.getters['conversationPage/getHasEndReached'](
        this.boardPageFilterKey
      );
    },
    conversationFilters() {
      let filters = {
        assigneeType: this.activeAssigneeTab,
        status: this.status ?? 'all',
        page: this.currentPage + 1,
        labels: this.label ? [this.label] : undefined,        
        conversationType: this.conversationType
          ? this.conversationType
          : undefined,
        folders: undefined,
        filterKey: this.boardPageFilterKey,
        boardKey: this.boardKey
      };
      filters[this.board.filter_key] = this.board.value;
      if(this.conversationInbox){
        filters['inboxId'] = this.conversationInbox;
      }
      if(this.teamId){
        filters['teamId'] = this.teamId;
      }
      return filters;
    },
    pageTitle() {
      if (this.inbox.name) {
        return this.inbox.name;
      }
      if (this.activeTeam.name) {
        return this.activeTeam.name;
      }
      if (this.label) {
        return `#${this.label}`;
      }
      if (this.conversationType === 'mention') {
        return this.$t('CHAT_LIST.MENTION_HEADING');
      }
      if (this.hasActiveFolders) {
        return this.activeFolder.name;
      }
      return this.$t('CHAT_LIST.TAB_HEADING');
    },
    conversationList() {
      const filters = this.conversationFilters;
      return [...this.allChatList(filters)];
    },
    activeFolder() {
      if (this.foldersId) {
        const activeView = this.folders.filter(
          view => view.id === Number(this.foldersId)
        );
        const [firstValue] = activeView;
        return firstValue;
      }
      return undefined;
    },
    activeTeam() {
      if (this.teamId) {
        return this.$store.getters['teams/getTeam'](this.teamId);
      }
      return {};
    },
    allConversationsSelected() {
      return (
        this.conversationList.length === this.selectedConversations.length &&
        this.conversationList.every(el =>
          this.selectedConversations.includes(el.id)
        )
      );
    },
    uniqueInboxes() {
      return [...new Set(this.selectedInboxes)];
    },    
    showEndOfListMessage() {
      return (
        this.conversationList.length &&
        this.hasCurrentPageEndReached &&
        !this.chatListLoading
      );
    },
    closingReasonChat() {
      return (
        this.$store.getters.getConversationById(
          this.closingReasonConversationId
        ) || {}
      );
    },
    closingReasonInbox() {
      return this.$store.getters['inboxes/getInbox'](
        this.closingReasonChat.inbox_id
      );
    },
    isOpportunityPipelinesEnabled() {
      return this.$store.getters['accounts/isFeatureEnabledonAccount'](
        this.$route.params.accountId,
        FEATURE_FLAGS.OPPORTUNITY_PIPELINES
      );
    },
  },
  watch: {
    activeTeam() {
      this.resetAndFetchData();
    },
    conversationInbox() {
      this.resetAndFetchData();
    },
    label() {
      this.resetAndFetchData();
    },
    conversationType() {
      this.resetAndFetchData();
    },
    board() {
      this.resetAndFetchData();
    },
    status() {
      this.resetAndFetchData();
    },
  },
  mounted() {
    this.resetAndFetchData();

    bus.$on('fetch_conversation_stats', () => {
      this.$store.dispatch('conversationStats/get', this.conversationFilters);
    });
    this.$root.$on('refresh_count_conversation', e => {
      this.fetchCountConversation();
    });

    bus.$on('conversations_force_reset_and_fetch_data', () => {
      if(this.$route.params.conversation_id !== undefined){
        return;
      }
      this.resetAndFetchData();
    });
  },
  methods: {
    resetAndFetchData() {
      this.resetBulkActions();
      this.$store.dispatch('conversationPage/reset');
      this.$store.dispatch('emptyAllConversations');
      this.$store.dispatch('clearConversationFilters');
      this.fetchConversations();
    },
    fetchConversations() {
      this.chatListLoading = true;
      this.$store
        .dispatch('fetchAllConversations', this.conversationFilters)
        .then(responseData => {
          const allCount = responseData?.meta?.all_count;
          if (typeof allCount === 'number') {
            this.count = allCount;
          }
          this.$emit('conversation-load');
          this.chatListLoading = false;
        }).catch(() => {
          this.chatListLoading = false;
        });

    },
    fetchFilteredConversations(payload) {
      this.chatListLoading = true;
      let page = this.currentFiltersPage + 1;
      this.$store
        .dispatch('fetchFilteredConversations', {
          queryData: filterQueryGenerator(payload),
          page,
          filterKey: this.boardPageFilterKey,
          boardKey: this.boardKey
        })
        .then(responseData => {
          const allCount = responseData?.meta?.all_count;
          if (typeof allCount === 'number') {
            this.count = allCount;
          }
          this.$emit('conversation-load');
          this.chatListLoading = false;
        }).catch(() => {
          this.chatListLoading = false;
        });
      this.showAdvancedFilters = false;
    },
    fetchSavedFilteredConversations(payload) {
      this.chatListLoading = true;
      let page = this.currentFiltersPage + 1;
      this.$store
        .dispatch('fetchFilteredConversations', {
          queryData: payload,
          page,
          filterKey: this.boardPageFilterKey,
          boardKey: this.boardKey
        })
        .then(responseData => {
          const allCount = responseData?.meta?.all_count;
          if (typeof allCount === 'number') {
            this.count = allCount;
          }
          this.$emit('conversation-load');
          this.chatListLoading = false;
        }).catch(() => {
          this.chatListLoading = false;
        });
    },
    updateAssigneeTab(selectedTab) {
      if (this.activeAssigneeTab !== selectedTab) {
        this.resetBulkActions();
        bus.$emit('clearSearchInput');
        this.activeAssigneeTab = selectedTab;
        if (!this.currentPage) {
          this.fetchConversations();
        }
      }
    },
    resetBulkActions() {
      this.selectedConversations = [];
      this.selectedInboxes = [];
    },
    openLastSavedItemInFolder() {
      const lastItemOfFolder = this.folders[this.folders.length - 1];
      const lastItemId = lastItemOfFolder.id;
      this.$router.push({
        name: 'folder_conversations',
        params: { id: lastItemId },
      });
    },
    openLastItemAfterDeleteInFolder() {
      if (this.folders.length > 0) {
        this.openLastSavedItemInFolder();
      } else {
        this.$router.push({ name: 'home' });
        this.fetchConversations();
      }
    },
    isConversationSelected(id) {
      return this.selectedConversations.includes(id);
    },
    selectConversation(conversationId, inboxId) {
      this.selectedConversations.push(conversationId);
      this.selectedInboxes.push(inboxId);
    },
    deSelectConversation(conversationId, inboxId) {
      this.selectedConversations = this.selectedConversations.filter(
        item => item !== conversationId
      );
      this.selectedInboxes = this.selectedInboxes.filter(
        item => item !== inboxId
      );
    },
    selectAllConversations(check) {
      if (check) {
        this.selectedConversations = this.conversationList.map(item => item.id);
        this.selectedInboxes = this.conversationList.map(item => item.inbox_id);
      } else {
        this.resetBulkActions();
      }
    },
    // Same method used in context menu, conversationId being passed from there.
    async onAssignAgent(agent, conversationId = null) {
      try {
        await this.$store.dispatch('bulkActions/process', {
          type: 'Conversation',
          ids: conversationId || this.selectedConversations,
          fields: {
            assignee_id: agent.id,
          },
        });
        this.selectedConversations = [];
        if (conversationId) {
          this.showAlert(
            this.$t(
              'CONVERSATION.CARD_CONTEXT_MENU.API.AGENT_ASSIGNMENT.SUCCESFUL',
              {
                agentName: agent.name,
                conversationId,
              }
            )
          );
        } else {
          this.showAlert(this.$t('BULK_ACTION.ASSIGN_SUCCESFUL'));
        }
      } catch (err) {
        this.showAlert(this.$t('BULK_ACTION.ASSIGN_FAILED'));
      }
    },
    async onAssignTeam(team, conversationId = null) {
      try {
        await this.$store.dispatch('assignTeam', {
          conversationId,
          teamId: team.id,
        });
        this.showAlert(
          this.$t(
            'CONVERSATION.CARD_CONTEXT_MENU.API.TEAM_ASSIGNMENT.SUCCESFUL',
            {
              team: team.name,
              conversationId,
            }
          )
        );
        if(this.board.key == 'teams'){
          this.$root.$emit('refresh_count_conversation', true);
        }
      } catch (error) {
        this.showAlert(
          this.$t('CONVERSATION.CARD_CONTEXT_MENU.API.TEAM_ASSIGNMENT.FAILED')
        );
      }
    },
    async assignPriority(priority, conversationId = null) {
      this.$store.dispatch('setCurrentChatPriority', {
        priority,
        conversationId,
      });
      this.$store
        .dispatch('assignPriority', { conversationId, priority })
        .then(() => {
          this.$track(CONVERSATION_EVENTS.CHANGE_PRIORITY, {
            newValue: priority,
            from: 'Context menu',
          });
          this.showAlert(
            this.$t('CONVERSATION.PRIORITY.CHANGE_PRIORITY.SUCCESSFUL', {
              priority,
              conversationId,
            })
          );
        });
    },
    async markAsUnread(conversationId) {
      try {
        await this.$store.dispatch('markMessagesUnread', {
          id: conversationId,
        });
      } catch (error) {
        //
      }
    },
    // Same method used in context menu, conversationId being passed from there.
    async onAssignLabels(labels, conversationId = null) {
      try {
        await this.$store.dispatch('bulkActions/process', {
          type: 'Conversation',
          ids: conversationId || this.selectedConversations,
          labels: {
            add: labels,
          },
        });
        this.selectedConversations = [];
        if (conversationId) {
          this.showAlert(
            this.$t(
              'CONVERSATION.CARD_CONTEXT_MENU.API.LABEL_ASSIGNMENT.SUCCESFUL',
              {
                labelName: labels[0],
                conversationId,
              }
            )
          );
        } else {
          this.showAlert(this.$t('BULK_ACTION.LABELS.ASSIGN_SUCCESFUL'));
        }
      } catch (err) {
        this.showAlert(this.$t('BULK_ACTION.LABELS.ASSIGN_FAILED'));
      }
    },
    async onUpdateConversations(status) {
      try {
        await this.$store.dispatch('bulkActions/process', {
          type: 'Conversation',
          ids: this.selectedConversations,
          fields: {
            status,
          },
        });
        this.selectedConversations = [];
        this.showAlert(this.$t('BULK_ACTION.UPDATE.UPDATE_SUCCESFUL'));
      } catch (err) {
        this.showAlert(this.$t('BULK_ACTION.UPDATE.UPDATE_FAILED'));
      }
    },
    async toggleConversationStatus(conversationId, status, snoozedUntil) {
      if (
        status === wootConstants.STATUS_TYPE.RESOLVED &&
        this.isOpportunityPipelinesEnabled
      ) {
        const conversation = this.$store.getters.getConversationById(
          conversationId
        );
        const openOpportunity = await fetchOpenOpportunityForConversation(
          conversation?.primary_key
        );
        if (openOpportunity) {
          this.resolveBlockedOpportunity = openOpportunity;
          this.showResolveBlockedModal = true;
          return;
        }
      }

      if (
        isConversationStatusChangeBlocked({
          store: this.$store,
          conversationId,
          status,
        })
      ) {
        const options =
          status === wootConstants.STATUS_TYPE.SNOOZED
            ? { snooze: snoozedUntil }
            : {};
        this.openClosingReasonPopup(conversationId, options);
        return;
      }

      this.$store
        .dispatch('toggleStatus', {
          conversationId,
          status,
          snoozedUntil,
        })
        .then(() => {
          this.showAlert(this.$t('CONVERSATION.CHANGE_STATUS'));
          this.isLoading = false;
          if(this.board.key == 'status'){
            this.$root.$emit('refresh_count_conversation', true);
          }
        }).catch(error => {
          this.showAlert(
            parseAPIErrorResponse(error) ||
              this.$t('CONVERSATION.CHANGE_STATUS_FAILED')
          );
          this.isLoading = false;
        });
    },
    allSelectedConversationsStatus(status) {
      if (!this.selectedConversations.length) return false;
      return this.selectedConversations.every(item => {
        return this.$store.getters.getConversationById(item).status === status;
      });
    },
    onContextMenuToggle(state) {
      this.isContextMenuOpen = state;
    },
    openClosingReasonPopup(conversationId, options = {}) {
      this.closingReasonConversationId = conversationId;
      this.closingReasonOptions = options;
      this.showClosingReasonPopup = true;
    },
    hideClosingReasonPopup() {
      this.showClosingReasonPopup = false;
      this.closingReasonConversationId = null;
      this.closingReasonOptions = {};
    },
    closeResolveBlockedModal() {
      this.showResolveBlockedModal = false;
      this.resolveBlockedOpportunity = null;
    },
    loadMoreConversations() {
      if (!this.hasAppliedFiltersOrActiveFolders) {
        this.fetchConversations();
      }
      if (this.hasAppliedFilters) {
        this.fetchFilteredConversations(this.appliedFilters);
      }
    },
    fetchCountConversation() {
      ConversationApi.meta({...this.conversationFilters, ...{onlyMetaAllCount: true, ignoreCache: true}}).then(response => {
        const allCount = response.data?.meta?.all_count;
        if (typeof allCount === 'number') {
          this.count = allCount;
        }
      });      
    },
    onDrop(evt) {
      let conversation = evt.dataTransfer.getData('conversation');
      conversation = JSON.parse(conversation);
      if(this.board.key == 'status'){
        if(conversation.status == this.status){
          return;
        }
        this.toggleConversationStatus(conversation.id, this.board.value, null);
      }else if(this.board.key == 'teams'){
        if(conversation?.team_id == this.board.value){
          return;
        }
        const team = (this.board.value > 0 ? this.$store.getters['teams/getTeam'](this.board.value) : {id: 0});
        this.onAssignTeam(team, conversation.id);
      }
    },
  },
};
</script>

<style scoped lang="scss">
@import '~dashboard/assets/scss/woot';

.spinner {
  margin-top: var(--space-normal);
  margin-bottom: var(--space-normal);
}

.board-conversations {
  display: flex;
  padding-left: 10px;
  flex-flow: column;
  flex: 1;
  margin-bottom: 64px;
  .conversations-title {
    padding: 5px 0 5px 1.6rem;
    background: #ebf0f5;
    position: relative;
    width: 100%;
    > h2 {
      font-size: 12px;
      color: var(--s-600);
      margin-bottom: 0;
    }
  }
  .conversations-list {
    min-width: 338px;
    border: 1px solid #ebf0f5;
  }
}

.conversations-list {
  // Prevent the list from scrolling if the submenu is opened
  &.is-context-menu-open {
    overflow: hidden !important;
  }
}

.conversations-list-wrap {
  flex-shrink: 0;
  width: 34rem;
  overflow: hidden;
  @include breakpoint(large up) {
    width: 36rem;
  }
  @include breakpoint(xlarge up) {
    width: 35rem;
  }
  @include breakpoint(xxlarge up) {
    width: 38rem;
  }
  @include breakpoint(xxxlarge up) {
    flex-basis: 46rem;
  }

  &.hide {
    display: none;
  }

  &.list--full-width {
    width: 100%;
    @include breakpoint(xxxlarge up) {
      flex-basis: 100%;
    }
  }
}
.filter--actions {
  display: flex;
  align-items: center;
}

.btn-filter {
  margin: 0 var(--space-smaller);
}

.filter__applied {
  padding: 0 0 var(--space-slab) 0 !important;
  border-bottom: 1px solid var(--color-border);
}

.delete-custom-view__button {
  margin-right: var(--space-normal);
}
</style>
